import * as React from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Modal} from 'shells/modal/modal';
import {SelectionDropdownAndList} from 'shells/selection-modal/selection-dropdown-and-list';
import {Body} from 'shells/typography/body';

import type {Option} from '../../scheduler/assignment/users-teams-selection-modal';

import {ShareSelectionComponent} from '../share-selection';
import type {ShareType} from '../share-selection';

type Props = {
    onSave: (arg1: boolean, arg2: Array<Option>) => void;
    onClose: () => any;
    isSaving: boolean;
    title: string;
    isOpen: boolean;
    shareType: ShareType;
    selectedOptions: Option[];
    remainingOptions: Option[];
    listName: string;
};

export function FilterShareModal(props: Props) {
    const [shareType, setShareType] = React.useState(props.shareType);
    const [selectedOptions, setSelectedOptions] = React.useState<Option[]>(props.selectedOptions);

    const currentRemainingOptions = props.remainingOptions.filter(
        (option) => !selectedOptions.find((selected) => selected.value === option.value)
    );

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onClose}
            maxWidth={452}
            fitHeight={true}
            noPadding={true}
            closeButton={false}
        >
            <div className='py-16 px-16 full-height'>
                <StackLegacy isFullHeight={true} spacing={16}>
                    <Body size='xlarge' isBold={true}>
                        {props.title}
                    </Body>
                    <ShareSelectionComponent
                        shareType={shareType}
                        onChangeShareType={setShareType}
                        canShareWithIndividuals={true}
                    />
                    <Body size='small' color='light'>
                        {shareTypeSubheader(shareType, props.listName)}
                    </Body>
                    {shareType === 'individuals' && (
                        <SelectionDropdownAndList
                            remainingOptions={currentRemainingOptions}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions}
                            selectPlaceholder='Select a user or team'
                            subtitle='Share with…'
                            nothingFoundState='No users or teams currently selected'
                            isShort={true}
                        />
                    )}
                    <div className='flex justify-sb'>
                        <Button
                            onClick={props.onClose}
                            variant='text-secondary'
                            size='small'
                            noPadding={true}
                        >
                            Cancel
                        </Button>
                        <SaveButton
                            disabled={shareType === 'individuals' && selectedOptions.length < 1}
                            isSaving={props.isSaving}
                            // @ts-expect-error
                            onClick={() => {
                                if (shareType !== 'individuals') {
                                    setSelectedOptions([]);
                                }
                                props.onSave(
                                    shareType !== 'nobody',
                                    shareType === 'individuals' ? selectedOptions : []
                                );
                            }}
                            onClearSaveComplete={props.onClose}
                            saveCompleteTimeout={2000}
                            buttonText='Confirm'
                        />
                    </div>
                </StackLegacy>
            </div>
        </Modal>
    );
}

function shareTypeSubheader(shareType: ShareType, listName: string): string | React.ReactNode {
    switch (shareType) {
        case 'everyone':
            return 'Visible to everyone in my organization';
        case 'individuals':
            return (
                <span>
                    Select specific users who can access the{' '}
                    <Body size='small' isBold={true}>
                        {listName}
                    </Body>{' '}
                    list
                </span>
            );
        case 'nobody':
        default:
            return 'Only visible to me';
    }
}
